import React, { useMemo } from "react";
import { Link, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ChargeCalculationRow from "src/components/billing/ChargeCalculationRow";
import useAuth from "src/hooks/useAuth";
import getBillingLabel from "src/utils/billing/getBillingLabel";
import {
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "src/components/project/settings/utils/billingTypes";

const FLAT_RATE_BILLING_TYPE = "Flat Rate/As Quoted (Internal Work Orders)";
const NOT_APPLICABLE_BILLING_TYPE = "Not Applicable";
const INTERNAL_BILLING_TYPES = [
  FLAT_RATE_BILLING_TYPE,
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
  NOT_APPLICABLE_BILLING_TYPE,
];

const isTimeAndMaterialsBillingType = billingType =>
  [TIME_AND_MATERIALS_BLENDED, TIME_AND_MATERIALS_ITEMIZED].includes(billingType);

export default function InternalWorkOrderChargesInfoModal({
  open = true,
  onClose,
  internalWorkOrder,
  billingType,
  savedBillingType,
  flatRateAmount,
  onBillingTypeChange,
  onFlatRateAmountChange,
  onSaveBillingConfiguration,
  onEditBillableQuantities,
  loading = false,
  hasBillingConfigurationChanges = false,
}) {
  const location = useLocation();
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const charges = internalWorkOrder?.internalWorkOrderCharges || {};
  const chargeKeys = useMemo(
    () => Object.keys(charges).filter(key => !["calculated_at", "total"].includes(key)),
    [charges]
  );
  const hasCharges = chargeKeys.length > 0;
  const showFlatRateAmountField = billingType === FLAT_RATE_BILLING_TYPE;
  const showTimeAndMaterialsActions = isTimeAndMaterialsBillingType(billingType);
  const hasPendingBillingTypeChange = billingType !== savedBillingType;
  const shouldShowCharges = hasCharges && !hasPendingBillingTypeChange;
  const shouldShowChargeBreakdown = shouldShowCharges && !showFlatRateAmountField;

  const getEditRatesTo = () => {
    const returnParams = new URLSearchParams(location.search);
    returnParams.set("returnedFromEditRates", "true");
    const returnTo = `${location.pathname}?${returnParams.toString()}${location.hash || ""}`;
    const searchParams = new URLSearchParams();
    searchParams.set("info", "true");
    searchParams.set("tab", "settings");
    searchParams.set("billingConfig", "billing-types");
    searchParams.set("search", chargeKeys.map(key => getBillingLabel(key)).join(","));
    searchParams.set("returnTo", returnTo);

    return {
      search: searchParams.toString(),
    };
  };

  const helperText = (() => {
    if (hasPendingBillingTypeChange && showTimeAndMaterialsActions) {
      return "Save changes first to clear any previous billable quantities, then edit rates and billable quantities separately for this billing type.";
    }
    if (hasPendingBillingTypeChange && showFlatRateAmountField) {
      return "Save changes first to clear any previous billable quantities, then set the flat rate amount for this internal work order.";
    }
    if (hasPendingBillingTypeChange) {
      return "Save changes first to clear any previous billable quantities for this billing type.";
    }
    if (showFlatRateAmountField && hasCharges) {
      return "Update the billing type or adjust the flat rate amount for this internal work order.";
    }
    if (showFlatRateAmountField) {
      return "Select the billing type and set the flat rate amount for this internal work order.";
    }
    if (showTimeAndMaterialsActions && hasCharges) {
      return "This screen shows how the internal work order charges for this transaction were calculated.";
    }
    if (showTimeAndMaterialsActions) {
      return "Add billable quantities for this internal work order to calculate the charges.";
    }
    return "This internal work order is currently not billed. Select a billing type to add charges.";
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          Internal Work Order Charges
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography color="text.secondary" px={3} textAlign="center" fontSize={15}>
            {helperText}
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            label="Billing Type"
            value={billingType}
            onChange={event => onBillingTypeChange(event.target.value)}
            disabled={!isInternalStaff}
          >
            {INTERNAL_BILLING_TYPES.map(option => (
              <MenuItem key={option} value={option}>
                {option === FLAT_RATE_BILLING_TYPE ? "Flat Rate/As Quoted" : option}
              </MenuItem>
            ))}
          </TextField>
          {showFlatRateAmountField && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Flat Rate Amount"
              value={flatRateAmount}
              onChange={event => onFlatRateAmountChange(event.target.value)}
              disabled={!isInternalStaff}
            />
          )}
        </Box>
        {shouldShowChargeBreakdown && (
          <Box display="flex" flexDirection="column" gap={2} mt={3}>
            {chargeKeys.map(key => {
              const row = charges[key];
              const label = getBillingLabel(key);
              const isHundredweight = key === "hundredweight";
              const hasMinimumCharge = row?.notes?.includes("Minimum charge applied");

              return (
                <ChargeCalculationRow
                  key={key}
                  label={label}
                  rateUsed={row?.rate_used}
                  quantity={row?.quantity}
                  total={row?.total}
                  notes={row?.notes}
                  isHundredweight={isHundredweight && !hasMinimumCharge}
                />
              );
            })}
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} gap={1}>
          {isInternalStaff && showTimeAndMaterialsActions ? (
            <Button
              variant="text"
              component={Link}
              to={getEditRatesTo()}
              disabled={hasBillingConfigurationChanges}
            >
              Edit Rates
            </Button>
          ) : (
            <Box />
          )}
          <Box display="flex" gap={1} ml="auto">
            {isInternalStaff && showFlatRateAmountField && (
              <Button
                variant="outlined"
                color="primary"
                onClick={onSaveBillingConfiguration}
                disabled={!hasBillingConfigurationChanges || loading}
              >
                {shouldShowCharges ? "Save Changes" : "Save Billing Configuration"}
              </Button>
            )}
            {isInternalStaff && showTimeAndMaterialsActions && (
              <Button
                variant="outlined"
                color="primary"
                onClick={onEditBillableQuantities}
                disabled={hasBillingConfigurationChanges}
              >
                {shouldShowCharges ? "Edit Billable Quantities" : "Add Billable Quantities"}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
